/**
 * @module @shared/api/supabase
 * Geteilter Supabase-Cookie-Client der Admin-App (Cross-Subdomain-SSO über
 * `.gmolike.me`). Erzeugt via SDK aus der zentralen `globalLoginConfig`.
 * Alle Entity-`queryFn`s greifen über `supabase.schema('identity').from(...)` zu.
 *
 * Hinweis: Existiert das zentrale `gmolike-id`-Projekt noch nicht, schlagen
 * Requests fehl — die Entities rendern dann sauber den Error-State (kein Crash).
 */
import { createGlobalLoginClient } from '@repo/global-login-sdk';
import type { SupabaseClient } from '@supabase/supabase-js';
import { globalLoginConfig } from '@shared/config';

/** Singleton-Client mit subdomain-weiter Session (PKCE, Cookie-Storage). */
export const supabase: SupabaseClient = createGlobalLoginClient(globalLoginConfig);

/** Name des zentralen Identitäts-/Autorisierungs-Schemas in Supabase. */
export const IDENTITY_SCHEMA = 'identity' as const;

/** Name des Rechtstext-Schemas (Migration `0013`, ADR-0010). */
export const LEGAL_SCHEMA = 'legal' as const;

/**
 * Bequemer Zugriff auf das `identity`-Schema. Entities nutzen
 * `identityFrom('apps')` etc. statt `supabase.schema('identity').from(...)`.
 *
 * @param table Tabellenname innerhalb des `identity`-Schemas.
 * @returns Ein PostgREST-Query-Builder für die Tabelle.
 */
export function identityFrom(
  table: string,
): ReturnType<ReturnType<SupabaseClient['schema']>['from']> {
  return supabase.schema(IDENTITY_SCHEMA).from(table);
}

/**
 * Bequemer Zugriff auf das `legal`-Schema (Rechtstext-Bausteine, Profile,
 * Overrides, Versionshistorie). Gegenstück zu {@link identityFrom}.
 *
 * Hinweis: `legal` muss in der PostgREST-Allowlist stehen (Settings → API →
 * Exposed schemas), sonst antwortet die REST-API mit 404 auf existierende
 * Tabellen — siehe Kopfkommentar der Migration `0013`.
 *
 * @param table Tabellenname innerhalb des `legal`-Schemas.
 * @returns Ein PostgREST-Query-Builder für die Tabelle.
 */
export function legalFrom(table: string): ReturnType<ReturnType<SupabaseClient['schema']>['from']> {
  return supabase.schema(LEGAL_SCHEMA).from(table);
}

/**
 * UUID des aktuell angemeldeten Nutzers — Pflichtfeld beim Schreiben in
 * `identity.audit_log`: die RLS-Policy `audit_log_admin_insert` (Migration `0010`)
 * verlangt `actor = auth.uid()`, damit niemand Audit-Zeilen mit fremdem Akteur
 * anlegen kann.
 *
 * @returns Die Nutzer-UUID oder `null`, wenn keine Session existiert.
 */
export async function currentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.user.id ?? null;
}
